import { useQuery } from '@tanstack/react-query';
import API, { getMediaURL } from '../../../../context/API';
import { useActiveStoreId } from '../../../Stores/hooks';
import { requireActiveStoreId } from '../../../../utils/activeStore';
import { SoldOutItem } from '../types';
import { QUERY_KEYS } from '../constants';

interface SoldOutPage {
  items: SoldOutItem[];
  total: number;
}

function sortByUpdatedAt(data: SoldOutItem[]): SoldOutItem[] {
  return [...data].sort(
    (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
  );
}

async function fetchSoldOutItems(page: number, limit: number, search: string): Promise<SoldOutPage> {
  const storeId = requireActiveStoreId();
  const response = await API.get(
    `/items/stock?soldOut=true&page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&storeId=${storeId}`
  );
  const items = sortByUpdatedAt(
    response.data.data.map((item: SoldOutItem) => ({
      ...item,
      picture: getMediaURL(item.picture),
    }))
  );
  return { items, total: response.data.total };
}

export function useSoldOutQuery(search: string, page: number, pageSize: number, enabled = true) {
  const storeId = useActiveStoreId();

  return useQuery({
    queryKey: [QUERY_KEYS.SOLD_OUT_ITEMS, search, page, pageSize, storeId],
    queryFn: () => fetchSoldOutItems(page, pageSize, search),
    staleTime: 0,
    // Only reuse placeholder data from the same store, so switching stores
    // never briefly shows the previous store's sold-out items.
    placeholderData: (prevData, prevQuery) =>
      prevQuery?.queryKey[4] === storeId ? prevData : undefined,
    enabled: enabled && !!storeId,
  });
}
